/**
 * Cookie consent helper.
 *
 * Future tracking integrations (Google Analytics, Meta Pixel, TikTok Pixel,
 * Hotjar, etc.) MUST gate themselves through this module — never load a
 * tracking script unconditionally.
 *
 * Usage when adding GA (example):
 *
 *   import { hasConsent, onConsentChange } from '@/lib/consent';
 *
 *   function loadGA() {
 *     if (!hasConsent('analytics')) return;
 *     // ... inject GA script ...
 *   }
 *
 *   loadGA();
 *   onConsentChange(loadGA); // re-evaluate when the user updates choices
 */

export type ConsentCategory = 'required' | 'personalization' | 'marketing' | 'analytics';

export interface CookiePreferences {
  required: boolean;
  personalization: boolean;
  marketing: boolean;
  analytics: boolean;
}

// Bumped to v2 (2026-06-16) — banner copy/options changed (Meta Pixel added),
// so we invalidate previous consents and re-prompt everyone.
// Bump this key whenever the banner content/options change to force re-prompt.
export const CONSENT_STORAGE_KEY = 'buxap_cookie_consent_v2';
export const CONSENT_EVENT = 'buxap:consentchange';

/** How long a saved consent remains valid before re-prompting the user. */
export const CONSENT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredConsent extends CookiePreferences {
  /** Epoch ms when the user saved the choice. Used for 30-day expiry. */
  savedAt?: number;
}

export const DEFAULT_PREFS: CookiePreferences = {
  required: true,
  personalization: false,
  marketing: false,
  analytics: false,
};

export function getStoredConsent(): CookiePreferences | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    // Expire after 30 days so we re-prompt the user.
    if (parsed.savedAt && Date.now() - parsed.savedAt > CONSENT_TTL_MS) {
      try { localStorage.removeItem(CONSENT_STORAGE_KEY); } catch { /* ignore */ }
      return null;
    }
    const { savedAt: _omit, ...prefs } = parsed;
    return prefs as CookiePreferences;
  } catch {
    return null;
  }
}

/** Returns true only if the user has affirmatively granted this category. */
export function hasConsent(category: Exclude<ConsentCategory, 'required'>): boolean {
  const prefs = getStoredConsent();
  return !!prefs && prefs[category] === true;
}

/** Persist consent and notify all listeners so trackers can (un)load in real time. */
export function saveConsent(prefs: CookiePreferences): void {
  if (typeof window === 'undefined') return;
  const final: CookiePreferences = { ...prefs, required: true };
  const stored: StoredConsent = { ...final, savedAt: Date.now() };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // ignore (quota / private mode)
  }
  window.dispatchEvent(new CustomEvent<CookiePreferences>(CONSENT_EVENT, { detail: final }));
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(cb: (prefs: CookiePreferences) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event) => cb((e as CustomEvent<CookiePreferences>).detail);
  window.addEventListener(CONSENT_EVENT, handler);
  return () => window.removeEventListener(CONSENT_EVENT, handler);
}
